import Handlebars from "handlebars";
import html2pdf from "html2pdf.js";

/**
 * Load invoice template based on current language
 */
export const loadInvoiceTemplate = async () => {
  const currentLang = localStorage.getItem('i18nextLng') || 'en';
  const lang = currentLang.split('-')[0];
  const templatePath = `/invoicetemplates/template-${lang}.html`;
  
  try {
    const response = await fetch(templatePath);
    if (!response.ok) throw new Error('Template not found');
    return await response.text();
  } catch (error) {
    console.error("Error loading template:", error);
    const fallbackResponse = await fetch('/invoicetemplates/template-en.html');
    return await fallbackResponse.text();
  }
};

/**
 * Render invoice template with data
 * @param {Object} invoice - The invoice data
 * @param {Object} storeInfo - Company/store information
 * @param {Object} t - Translation function
 */
export const renderInvoiceTemplate = async (invoice, storeInfo, t) => {
  if (!invoice || !storeInfo) return null;

  const template = await loadInvoiceTemplate();
  const compiledTemplate = Handlebars.compile(template);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return "N/A";
    }
  };

  const customer = invoice.Customer || {};
  const lineItems = (invoice.LineItems || []).map(item => ({
    UpdatedProductName: item.DisplayName || item.UpdatedProductName || item.ProductName || item.Name || '',
    Quantity: item.Quantity || 0,
    UnitPrice: parseFloat(item.UnitPrice || item.Price || 0).toFixed(2),
    TotalExcl: parseFloat(item.TotalExcl || 0).toFixed(2),
    VATAmount: parseFloat(item.TotalVAT || 0).toFixed(2),
    TotalIncl: parseFloat(item.TotalInclVAT || item.TotalIncl || 0).toFixed(2)
  }));

  // Build payment methods section
  let paymentMethodsSection = '';
  if (invoice.Payments && invoice.Payments.length > 0) {
    const paymentMap = {};
    invoice.Payments.forEach((payment) => {
      const methodName = payment.PaymentMethodName || payment.PaymentMethod?.Name || 'Unknown Method';
      const amount = Number(payment.Amount || 0);
      
      if (paymentMap[methodName]) {
        paymentMap[methodName] += amount;
      } else {
        paymentMap[methodName] = amount;
      }
    });

    paymentMethodsSection = Object.entries(paymentMap)
      .map(([method, amount]) => 
        `<div class="total-row"><span>${method}:</span><strong>€${amount.toFixed(2)}</strong></div>`
      )
      .join('');
  }

  const data = {
    // Company Info
    CompanyName: storeInfo?.name || "",
    CompanyVAT: storeInfo?.vat || "",
    CompanyEmail: storeInfo?.email || "",
    CompanyStreetAddress: storeInfo?.address || "",
    CompanyCityCountry: [storeInfo?.postCode, storeInfo?.city, storeInfo?.country].filter(Boolean).join(' ') || "",
    CompanyPhone: storeInfo?.phone || "",
    CompanyIBAN: storeInfo?.iban || "",
    
    // Customer Info
    CustomerName: customer.Name || customer.CompanyName || invoice.CustomerName || "",
    CustomerVAT: customer.VATNumber || "",
    CustomerNumber: customer.Id || "",
    CustomerStreetAddress: `${customer.Street || ""} ${customer.Box || ""}`.trim() || "",
    CustomerCityCountry: [customer.PostCode, customer.City, customer.Country].filter(Boolean).join(' ') || "",
    
    // Invoice Info
    InvoiceNumber: invoice.InvoiceNumber || "",
    InvoiceDate: formatDate(invoice.IssueDate || invoice.InvoiceDate || invoice.CreatedDate),
    DueDate: formatDate(
      // If both PaymentDueDate and DueDate exist, use invoice date (created date)
      (invoice.PaymentDueDate && invoice.DueDate) 
        ? (invoice.IssueDate || invoice.InvoiceDate || invoice.CreatedDate)
        : (invoice.PaymentDueDate || invoice.DueDate || invoice.IssueDate || invoice.InvoiceDate || invoice.CreatedDate)
    ),
    PaymentMethod: invoice.PaymentMethod || "N/A",
    PaymentMethodsSection: paymentMethodsSection,
    VAT: invoice.VATPercentage || 21,
    PaymentStatus: invoice.Status || invoice.PaymentStatus || "N/A",
    OutstandingSection: "",
    
    // Line Items
    LineItems: lineItems,
    ProductRows: lineItems.map(item => `
      <tr>
        <td>${item.UpdatedProductName}</td>
        <td class="text-center">${item.Quantity}</td>
        <td class="text-right">€${item.UnitPrice}</td>
        <td class="text-right">€${item.TotalExcl}</td>
        <td class="text-right">€${item.VATAmount}</td>
        <td class="text-right">€${item.TotalIncl}</td>
      </tr>
    `).join(''),
    TotalQuantity: lineItems.reduce((sum, item) => sum + Number(item.Quantity || 0), 0),
    
    // Totals
    TotalExcl: parseFloat(invoice.InvoiceSubTotal || 0).toFixed(2),
    TotalVAT: parseFloat(invoice.InvoiceTotalVATAmount || 0).toFixed(2),
    TotalAmount: parseFloat(invoice.InvoiceTotalAmount || 0).toFixed(2),
    GrandTotal: parseFloat(invoice.InvoiceTotalAmount || 0).toFixed(2),
    TotalIncl: parseFloat(invoice.InvoiceTotalAmount || 0).toFixed(2),
    
    // Payment Info
    PaidAmount: parseFloat(invoice.InvoicePaidAmount || 0).toFixed(2),
    DueAmount: parseFloat(invoice.InvoiceDueAmount || 0).toFixed(2),
    InvoicePaidAmount: parseFloat(invoice.InvoicePaidAmount || 0).toFixed(2),
    InvoiceDueAmount: parseFloat(invoice.InvoiceDueAmount || 0).toFixed(2),
    
    // Discount
    DiscountRow: invoice.InvoiceDiscount > 0 ? 
      `<tr class="discount-row">
        <td colspan="5" style="text-align:right; font-style: italic;">
          ${t ? t('discount', 'Discount') : 'Discount'}:
        </td>
        <td class="text-right">- €${parseFloat(invoice.InvoiceDiscount || 0).toFixed(2)}</td>
      </tr>` : '',
    
    // Translations
    T_INVOICE: t ? t('invoice', 'INVOICE') : 'INVOICE',
    T_BILLED_TO: t ? t('billed_to', 'Billed to') : 'Billed to',
    T_VAT: t ? t('vat', 'VAT') : 'VAT',
    T_ADDRESS: t ? t('address', 'Address') : 'Address',
    T_IBAN: t ? t('iban', 'IBAN') : 'IBAN',
    T_INVOICE_NUMBER: t ? t('invoice_number', 'Invoice Number') : 'Invoice Number',
    T_INVOICE_DATE: t ? t('invoice_date', 'Invoice Date') : 'Invoice Date',
    T_DUE_DATE: t ? t('due_date', 'Due Date') : 'Due Date',
    T_PAYMENT_METHOD: t ? t('payment_method', 'Payment Method') : 'Payment Method',
    T_DESCRIPTION: t ? t('description', 'Description') : 'Description',
    T_QTY: t ? t('qty', 'Qty') : 'Qty',
    T_UNIT_PRICE: t ? t('unit_price', 'Unit Price') : 'Unit Price',
    T_SUBTOTAL: t ? t('subtotal', 'Subtotal') : 'Subtotal',
    T_VAT_AMOUNT: t ? t('vat_amount', 'VAT Amount') : 'VAT Amount',
    T_TOTAL: t ? t('total', 'Total') : 'Total',
    T_GRAND_TOTAL: t ? t('grand_total', 'Grand Total') : 'Grand Total',
    T_PAID: t ? t('paid', 'Paid') : 'Paid',
    T_DUE: t ? t('due', 'Due') : 'Due',
    
    // Optional
    CustomText: invoice.CustomText || "",
    CustomTextSection: invoice.CustomText ? `<div class="notes-section"><h3>Notes</h3><p>${invoice.CustomText}</p></div>` : "",
    PageNumber: 1,
    TotalPages: 1
  };

  return compiledTemplate(data);
};

/**
 * Generate PDF from HTML
 * @param {string} html - The HTML content
 * @param {string} filename - The PDF filename
 */
export const generateInvoicePDF = async (html, filename) => {
  const opt = {
    margin: 0,
    filename: filename || 'invoice.pdf',
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { 
      scale: 2, 
      useCORS: true, 
      logging: false,
      width: 794,
      windowWidth: 794,
      letterRendering: true
    },
    jsPDF: { 
      unit: 'mm', 
      format: 'a4', 
      orientation: 'portrait' 
    },
    pagebreak: { 
      mode: ['avoid-all', 'css', 'legacy'],
      before: '.page-break-before',
      after: '.page-break-after',
      avoid: ['tr', 'td', 'img']
    }
  };

  return await html2pdf().set(opt).from(html).outputPdf('blob');
};

/**
 * Complete workflow: render template and generate PDF
 * @param {Object} invoice - The invoice data
 * @param {Object} storeInfo - Company/store information
 * @param {Object} t - Translation function
 * @param {string} filename - Optional filename
 */
export const generateInvoicePDFComplete = async (invoice, storeInfo, t, filename) => {
  const html = await renderInvoiceTemplate(invoice, storeInfo, t);
  if (!html) {
    throw new Error('Failed to render invoice template');
  }
  
  const invoiceFilename = filename || `${invoice.InvoiceNumber || 'invoice'}.pdf`;
  return await generateInvoicePDF(html, invoiceFilename);
};

/**
 * Download invoice PDF (for download button functionality)
 * @param {Object} invoice - The invoice data
 * @param {Object} storeInfo - Company/store information
 * @param {Object} t - Translation function
 */
export const downloadInvoicePDF = async (invoice, storeInfo, t) => {
  const html = await renderInvoiceTemplate(invoice, storeInfo, t);
  if (!html) {
    throw new Error('Failed to render invoice template');
  }

  const filename = `${invoice.InvoiceNumber || 'invoice'}.pdf`;
  const opt = {
    margin: 0,
    filename: filename,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { 
      scale: 2, 
      useCORS: true, 
      logging: false,
      width: 794,
      windowWidth: 794,
      letterRendering: true
    },
    jsPDF: { 
      unit: 'mm', 
      format: 'a4', 
      orientation: 'portrait' 
    },
    pagebreak: { 
      mode: ['avoid-all', 'css', 'legacy'],
      before: '.page-break-before',
      after: '.page-break-after',
      avoid: ['tr', 'td', 'img']
    }
  };

  await html2pdf().set(opt).from(html).save();
};
